import {
  CadDocument,
  documentToDxf,
  documentToPdf,
  loadDocument,
  parseTagged,
  serializeEntities,
  solveDocument,
  type SolveSummary,
} from '@lakaran/engine';
import type { Constraint, Entity } from '@lakaran/geometry';

/**
 * Kernel tasks heavy enough to be worth running off the main thread.
 *
 * These are plain functions so the same code runs inside the worker and in the inline
 * fallback (tests, browsers without module workers). A future WASM kernel can implement
 * the same request/response shapes without touching the call sites.
 */
export type ExportFormat = 'dxf' | 'pdf';

export interface ExportRequest {
  kind: 'export';
  format: ExportFormat;
  /** `.lakar` project JSON produced by `serializeDocument`. */
  json: string;
}

export interface SolveRequest {
  kind: 'solve';
  /** Tagged JSON of only the constrained entities. */
  entities: string;
  /** Tagged JSON of the active constraints. */
  constraints: string;
}

export type KernelRequest = ExportRequest | SolveRequest;

export interface ExportResult {
  kind: 'export';
  filename: string;
  mime: string;
  /** UTF-8 text for DXF, bytes for PDF. */
  data: string | Uint8Array<ArrayBuffer>;
}

export interface SolveResultMessage extends SolveSummary {
  kind: 'solve';
  /** Tagged JSON of the entities the solver moved. */
  entities: string;
  elapsedMs: number;
}

export type KernelResponse = ExportResult | SolveResultMessage;

export function runTask(request: KernelRequest): KernelResponse {
  if (request.kind === 'export') {
    const doc = new CadDocument();
    loadDocument(doc, request.json);
    if (request.format === 'dxf') {
      return {
        kind: 'export',
        filename: 'drawing.dxf',
        mime: 'application/dxf',
        data: documentToDxf(doc),
      };
    }
    return {
      kind: 'export',
      filename: 'drawing.pdf',
      mime: 'application/pdf',
      data: documentToPdf(doc),
    };
  }

  const started = performance.now();
  const doc = new CadDocument();
  for (const entity of parseTagged<Entity[]>(request.entities)) doc.add(entity);
  for (const constraint of parseTagged<Constraint[]>(request.constraints)) {
    doc.addConstraint(constraint);
  }

  const { tx, summary } = solveDocument(doc);
  const moved: Entity[] = [];
  for (const op of tx.ops) {
    if (op.kind === 'update') moved.push(op.after);
  }

  return {
    kind: 'solve',
    entities: moved.length > 0 ? serializeEntities(moved) : '[]',
    elapsedMs: performance.now() - started,
    ...summary,
  };
}

export type { SolveSummary };
